/**
 * Painel de Notificações Persistentes
 * Mostra notificações que só desaparecem quando o usuário clica no X
 * Usa React Portal para garantir posicionamento correto
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Bell, X, CheckCircle, AlertCircle, Info, Play, Pause, Mic, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { axiosInstance } from '../App';

const NotificacoesPanel = ({ companyId, userId }) => {
  const [notificacoes, setNotificacoes] = useState([]);
  const [showPanel, setShowPanel] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(null);
  const audioRef = useRef(null);

  const fetchNotificacoes = useCallback(async () => {
    if (!companyId) return;
    try {
      const response = await axiosInstance.get(`/notificacoes/${companyId}`);
      setNotificacoes(response.data.filter(n => !n.lida));
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
    }
  }, [companyId]);

  useEffect(() => {
    fetchNotificacoes();
    const interval = setInterval(fetchNotificacoes, 30000);
    return () => clearInterval(interval);
  }, [fetchNotificacoes]);

  const marcarComoLida = async (notificacaoId) => {
    try {
      await axiosInstance.patch(`/notificacao/${notificacaoId}/lida`);
      setNotificacoes(prev => prev.filter(n => n.id !== notificacaoId));
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  const playAudio = (url) => {
    if (audioRef.current) {
      if (audioPlaying === url) {
        audioRef.current.pause();
        setAudioPlaying(null);
      } else {
        audioRef.current.src = url;
        audioRef.current.play();
        setAudioPlaying(url);
      }
    }
  };

  const getIcon = (tipo) => {
    switch (tipo) {
      case 'ORCAMENTO_ACEITO':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'ALERTA':
        return <AlertCircle className="w-5 h-5 text-orange-400" />;
      case 'PRE_ORCAMENTO_CRIADO':
        return <Info className="w-5 h-5 text-yellow-400" />;
      default:
        return <Info className="w-5 h-5 text-blue-400" />;
    }
  };

  const getBgColor = (tipo) => {
    switch (tipo) {
      case 'ORCAMENTO_ACEITO':
        return 'bg-green-500/10 border-green-500/30';
      case 'ALERTA':
        return 'bg-orange-500/10 border-orange-500/30';
      case 'PRE_ORCAMENTO_CRIADO':
        return 'bg-yellow-500/10 border-yellow-500/30';
      default:
        return 'bg-blue-500/10 border-blue-500/30';
    }
  };

  const naoLidas = notificacoes.length;

  // Renderizar mídias de uma notificação (foto + descrição + áudio agrupados)
  const MidiasNotificacao = ({ midias }) => {
    if (!midias || midias.length === 0) return null;
    return (
      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {midias.map((midia, idx) => (
          <div key={idx} style={{
            background: 'rgba(0,0,0,0.3)',
            borderRadius: 8,
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.08)'
          }}>
            {/* Foto */}
            {midia.foto_url && (
              <img
                src={midia.foto_url}
                alt={`Mídia ${idx + 1}`}
                style={{ width: '100%', display: 'block', maxHeight: 200, objectFit: 'cover' }}
              />
            )}
            {/* Descrição */}
            {midia.descricao && (
              <div style={{ padding: '8px 12px', borderTop: midia.foto_url ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                <div style={{ fontSize: 10, color: '#888', marginBottom: 2 }}>Observação</div>
                <div style={{ fontSize: 13, color: '#e0e0e0' }}>{midia.descricao}</div>
              </div>
            )}
            {/* Áudio */}
            {midia.audio_url && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                  background: audioPlaying === midia.audio_url ? 'rgba(59,130,246,0.15)' : 'transparent'
                }}
                onClick={() => playAudio(midia.audio_url)}
              >
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: audioPlaying === midia.audio_url ? '#3b82f6' : 'rgba(59,130,246,0.2)',
                  color: audioPlaying === midia.audio_url ? '#fff' : '#3b82f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {audioPlaying === midia.audio_url ? <Pause size={14} /> : <Play size={14} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#e0e0e0' }}>
                    {audioPlaying === midia.audio_url ? 'Reproduzindo...' : 'REC'}
                  </div>
                </div>
                <Mic size={16} color="#888" />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const PainelNotificacoes = () => (
    <div
      data-testid="notificacoes-panel"
      className="fixed top-4 left-4 w-96 max-h-[600px] overflow-y-auto bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl z-[9999]"
    >
      <div className="sticky top-0 bg-zinc-900 p-4 border-b border-zinc-700 flex items-center justify-between">
        <h3 className="font-semibold text-white flex items-center gap-2">
          <Bell className="w-4 h-4" />
          Notificações
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowPanel(false)}
          className="text-zinc-400 hover:text-white h-6 w-6 p-0"
          data-testid="notificacoes-close-btn"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="p-2">
        {naoLidas === 0 ? (
          <div className="p-8 text-center text-zinc-500">
            <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Nenhuma notificação nova</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notificacoes.map((notif) => (
              <div
                key={notif.id}
                className={`relative p-4 rounded-lg border ${getBgColor(notif.tipo)} transition-all`}
              >
                <button
                  onClick={() => marcarComoLida(notif.id)}
                  className="absolute top-2 right-2 text-zinc-500 hover:text-white transition-colors"
                  title="Fechar notificação"
                >
                  <X className="w-4 h-4" />
                </button>

                <div className="flex gap-3 pr-6">
                  <div className="flex-shrink-0 mt-1">
                    {getIcon(notif.tipo)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-white text-sm">
                      {notif.titulo}
                    </h4>
                    <p className="text-zinc-400 text-sm mt-1 whitespace-pre-line">
                      {notif.mensagem}
                    </p>
                    {notif.observacoes && (
                      <p className="text-zinc-500 text-xs mt-1 italic">
                        Obs: {notif.observacoes}
                      </p>
                    )}

                    {/* Mídias do pré-orçamento */}
                    <MidiasNotificacao midias={notif.midias} />

                    <p className="text-zinc-600 text-xs mt-2">
                      {new Date(notif.created_at).toLocaleString('pt-BR')}
                    </p>

                    {notif.whatsapp_url && (
                      <a
                        href={notif.whatsapp_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-2 text-green-400 hover:text-green-300 text-sm"
                      >
                        Ver no WhatsApp
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Audio player oculto */}
      <audio ref={audioRef} onEnded={() => setAudioPlaying(null)} style={{ display: 'none' }} />
    </div>
  );

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowPanel(!showPanel)}
        className="relative text-zinc-400 hover:text-white"
        data-testid="notificacoes-bell-btn"
      >
        <Bell className="w-5 h-5" />
        {naoLidas > 0 && (
          <Badge
            className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs animate-pulse"
          >
            {naoLidas}
          </Badge>
        )}
      </Button>

      {showPanel && createPortal(<PainelNotificacoes />, document.body)}
    </>
  );
};

export default NotificacoesPanel;
